import { Popover } from 'antd';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import _ from 'lodash';
import { useTheme } from 'styled-components';
import { IconSvg } from 'shared/icon';
import { LocalStorageService } from 'services';

import { selectCurrentPartnerId } from 'app/userPreferences/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setCurrentPartnerId } from 'app/userPreferences/slices';
import { authApi } from 'app/auth/api';
import { useGetResourcePartnerQuery } from 'app/resource/api/partner';
import { PartnerSettings } from 'router/types';
import { skipToken } from '@reduxjs/toolkit/query';
import { isOwnUser } from 'utils/helpers';
import {
  StyledHeaderSelectClose, StyledHeaderSelectToggle, StyledHeaderSelectWrapper, StyledListItemViewMode,
} from 'app/layout/components/Layout.styled';
import { AdminSelect } from 'shared/formControl';
import { Current } from 'app/userPreferences/types';

const { useGetAuthUserPartnerAccessesQuery } = authApi;

export interface PartnersProps {
  partnerSettings?: PartnerSettings;
}

const Partners = ({ partnerSettings }:PartnersProps) => {
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(false);

  const { colors } = useTheme();
  const partnerId = useAppSelector(selectCurrentPartnerId);
  const { data: partner } = useGetResourcePartnerQuery(partnerId && partnerSettings?.viewMode ? { partnerId, skipGlobalLoading: true } : skipToken);

  const isVisible = useMemo(() => partnerSettings?.editMode || partnerSettings?.viewMode, [partnerSettings?.editMode, partnerSettings?.viewMode]);

  const { data: partners } = useGetAuthUserPartnerAccessesQuery(
    { includeMainPartner: !!partnerSettings?.includeMainPartner },
    {
      skip: !isVisible,
    },
  );

  const getCurrentPartner = useCallback(() => {
    const storageId = (LocalStorageService.get('current') as Current)?.partnerId;
    // include partners false case
    if (storageId && isOwnUser(storageId) && partnerSettings?.includeMainPartner) {
      return storageId;
    }

    const currentPartnerId = partners?.length ? partners[0].id : null;
    return !_.isNull(storageId) && partners?.find((el) => el.id === storageId) ? storageId : currentPartnerId;
  }, [partners, partnerSettings]);

  useEffect(() => {
    if (partners?.length) {
      const partId = getCurrentPartner();
      const currentData = LocalStorageService.get('current') || {};
      if (partId) {
        dispatch(setCurrentPartnerId(partId));
        LocalStorageService.set('current', {
          ...currentData,
          partnerId: partId,
        });
      }
    }
  }, [partners, getCurrentPartner, dispatch]);

  if (partnerSettings?.viewMode && partner?.name) {
    const { name } = partner;
    return (
      <Popover placement="bottomLeft" content={name} title="" rootClassName="description-popover">
        <StyledListItemViewMode $isBold>{name}</StyledListItemViewMode>
      </Popover>
    );
  }

  const openDropDown = () => {
    setOpen(true);
  };

  const closeDropDown = () => {
    setOpen(false);
  };

  const partnerChange = (id: number) => {
    const currentData = LocalStorageService.get('current') || {};

    LocalStorageService.set('current', {
      ...currentData,
      partnerId: id,
    });

    dispatch(setCurrentPartnerId(id));
    closeDropDown();
  };

  return (
    !!partners?.length && (
      <div>
        <StyledHeaderSelectToggle onClick={openDropDown}>
          <IconSvg icon="BrandIcon" width="1.4rem" height="1.4rem" color={colors.white} />
        </StyledHeaderSelectToggle>
        <StyledHeaderSelectWrapper $isOpen={open}>
          <AdminSelect onChange={partnerChange} value={partnerId} allowClear={false} data={partners} isToString={false} virtual={false} />
          <StyledHeaderSelectClose onClick={closeDropDown}>
            <IconSvg icon="CloseModalIcon" width="1.14rem" height="1.14rem" color={colors.white} />
          </StyledHeaderSelectClose>
        </StyledHeaderSelectWrapper>
      </div>
    )
  );
};

export default Partners;
